import {
	useAllRecurring,
	useConflicts,
	useDays,
	useFocuses,
	useOverdue,
	useProjects,
	useStore,
	useToday
} from "@mapcraft-tasks/client-store/react";
import { dayNodeId, isPlainDate, isTerminal, SYSTEM_NODE_IDS } from "@mapcraft-tasks/domain";
import { createRootRoute, createRoute, createRouter, Link, Outlet, redirect, useParams } from "@tanstack/react-router";
import { ArchiveTree } from "./components/archive-tree";
import { Checkbox } from "./components/checkbox";
import { shortDate } from "./components/date-badge";
import { dayTitle } from "./components/day-nav";
import { ActionRail } from "./components/mobile/action-rail";
import { MobileChrome } from "./components/mobile/chrome";
import { NodePage } from "./components/node-page";
import { Panels } from "./components/panels";
import { Sidebar } from "./components/sidebar";
import { Toasts } from "./components/toasts";
import { useToggleDone } from "./lib/complete";
import { usePhone } from "./lib/mobile";

/**
 * Маршруты объявлены КОДОМ, а не файлами.
 *
 * Файловый роутинг TanStack держит `routeTree.gen.ts` — генерируемый артефакт, который
 * пришлось бы исключать из biome, коммитить руками (коммиты в этом проекте делает человек)
 * и держать в актуальном состоянии. Ради восьми маршрутов это не окупается; типобезопасность
 * `Link`/`useParams`, ради которой роутер и взят, у кодовой формы ровно та же.
 *
 * **Zoom меняет URL** — поэтому работает «назад» браузера и ссылку на ноду можно прислать,
 * в том числе агентом (ui-spec Область 1).
 */

const rootRoute = createRootRoute({
	component: function Shell() {
		/*
		 * Раскладка панелями, как у Tana: подложка приложения видна по краям и в зазорах,
		 * а рабочее поле и правые панели — отдельные скруглённые панели поверх неё.
		 * Сайдбар прозрачный: он часть подложки, а не третья панель.
		 *
		 * На телефоне панель ровно одна — рабочее поле во весь экран. Зазор `--panels-inset`
		 * там обнулён, скругления сняты: экран телефона И ЕСТЬ панель, а подложка,
		 * видная по краям, была бы просто отнятыми пикселями. Сайдбар и панели дня
		 * уезжают в шторку (`MobileChrome`).
		 */
		const phone = usePhone();
		return (
			<MobileChrome>
				<div className="flex h-full gap-[var(--panels-inset)] bg-app p-[var(--panels-inset)]">
					{!phone && <Sidebar />}
					<Outlet />
					{!phone && <Panels />}
					<Toasts />
				</div>
			</MobileChrome>
		);
	}
});

const indexRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/",
	beforeLoad: () => {
		throw redirect({ to: "/today" });
	}
});

/** `/today` резолвится в day-ноду сегодняшней даты, но `ensureDay` при ПРОСМОТРЕ не зовётся. */
const todayRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/today",
	component: function TodayPage() {
		const today = useToday();
		return <NodePage rootId={dayNodeId(today)} title="Сегодня" subtitle={today} ensureDate={today} day={today} />;
	}
});

/**
 * Соседние дни — тот же экран, что и `/today`, только дата в адресе. Отдельный маршрут,
 * а не `/n/<id day-ноды>`: id дня считается из даты, и адрес `/day/2026-08-25` человек
 * набирает и правит руками, а uuid — нет. День при просмотре не заводится, как и сегодня;
 * `ensureDate` сработает на первую запись.
 */
const dayRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/day/$date",
	// Мусор в адресе уводит на сегодня: `dayNodeId` от невалидной даты дал бы
	// правдоподобный uuid несуществующей ноды и пустую страницу без объяснений.
	beforeLoad: ({ params }) => {
		if (!isPlainDate(params.date)) {
			throw redirect({ to: "/today" });
		}
	},
	component: function DayPage() {
		const { date } = useParams({ from: "/day/$date" });
		const today = useToday();
		// `key` — чтобы при листании состояние страницы (правка заголовка, инспектор,
		// выделение в аутлайне) не переезжало с одного дня на другой.
		return (
			<NodePage
				key={date}
				rootId={dayNodeId(date)}
				title={dayTitle(date, today)}
				subtitle={date}
				ensureDate={date}
				day={date}
			/>
		);
	}
});

const inboxRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/inbox",
	component: () => <NodePage rootId={SYSTEM_NODE_IDS.inbox} title="Входящие" />
});

const pinnedRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/pinned",
	component: () => <NodePage rootId={SYSTEM_NODE_IDS.pinned} title="Пины" />
});

/**
 * Фокус — не контейнер, а роль: страница собирается фильтром по ней, как «Проекты».
 * Поэтому здесь же и вся история заходов: открытые сверху, закрытые под ними,
 * свежие первыми. Отдельной страницы истории нет и не нужно — список один.
 */
const focusRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/focus",
	component: function FocusPage() {
		const focuses = useFocuses();
		const toggleDone = useToggleDone();
		return (
			<ListPage title="Фокус" empty="заходов в фокус ещё не было — аккорд `rf` делает ноду фокусом">
				{focuses.map((node) => (
					<li key={node.id} className="-mx-2 flex items-baseline gap-2 rounded-sm px-2 py-px hover:bg-hover">
						<Checkbox node={node} onToggle={() => toggleDone(node)} />
						<Link
							to="/n/$nodeId"
							params={{ nodeId: node.id }}
							className={`min-w-0 flex-1 truncate transition-colors ${
								isTerminal(node.status) ? "text-ink-faint line-through" : ""
							}`}
						>
							{node.title || "без названия"}
						</Link>
						{/* У закрытого фокуса справа стоит дата, КОГДА он закрыт, а не срок:
						    это единственное, что отличает один прошлый заход от другого. */}
						<span className="tabular shrink-0 text-xs font-light text-ink-muted">
							{node.completedAt ? formatCompleted(node.completedAt) : ""}
						</span>
					</li>
				))}
			</ListPage>
		);
	}
});

const completedFormat = new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });

function formatCompleted(at: Date): string {
	return completedFormat.format(at);
}

const nodeRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/n/$nodeId",
	component: function NodeRoute() {
		const { nodeId } = useParams({ from: "/n/$nodeId" });
		return <NodePage key={nodeId} rootId={nodeId} />;
	}
});

const projectsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/projects",
	component: function ProjectsPage() {
		const projects = useProjects();
		return (
			<ListPage title="Проекты" empty="проектов пока нет">
				{projects.map((project) => (
					<li key={project.id}>
						<Link
							to="/n/$nodeId"
							params={{ nodeId: project.id }}
							className="-mx-2 flex items-baseline justify-between gap-3 rounded-sm px-2 py-px transition-colors hover:bg-hover"
						>
							<span className="truncate">{project.title || "без названия"}</span>
							<span className="shrink-0 text-xs font-light text-ink-muted">{project.status}</span>
						</Link>
					</li>
				))}
			</ListPage>
		);
	}
});

const calendarRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/calendar",
	component: function CalendarPage() {
		const days = useDays();
		return (
			<ListPage title="Календарь" empty="дней ещё нет — они заводятся первой записью">
				{days.map((day) => (
					<li key={day.id}>
						<Link
							to="/day/$date"
							params={{ date: day.dayDate ?? day.title }}
							className="tabular -mx-2 block rounded-sm px-2 py-px transition-colors hover:bg-hover"
						>
							{day.dayDate}
						</Link>
					</li>
				))}
			</ListPage>
		);
	}
});

const recurringRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/recurring",
	component: function RecurringPage() {
		const items = useAllRecurring();
		return (
			<ListPage title="Повторяющиеся" empty="ничего не повторяется">
				{items.map((node) => (
					<li key={node.id}>
						<Link
							to="/n/$nodeId"
							params={{ nodeId: node.id }}
							className="-mx-2 flex items-baseline justify-between gap-3 rounded-sm px-2 py-px transition-colors hover:bg-hover"
						>
							<span className="truncate">{node.title}</span>
							<span className="shrink-0 text-xs font-light text-positive">
								↻ {node.recurrence?.count ?? 1}/{node.recurrence?.period}
							</span>
						</Link>
					</li>
				))}
			</ListPage>
		);
	}
});

/**
 * Просрочено — линза, не контейнер: ноды остаются где лежали, переноса нет. Две причины
 * в одном списке (`domain/views.overdue`): срок в прошлом и задача без срока, оставшаяся
 * в прошедшем дне. Справа — какая из них: «срок» или «день», и с какого числа.
 * Галочка прямо здесь — разбирать просрочку, не уходя со страницы.
 */
const overdueRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/overdue",
	component: function OverduePage() {
		const items = useOverdue();
		const today = useToday();
		const toggleDone = useToggleDone();
		return (
			<ListPage title="Просрочено" empty="ничего не просрочено">
				{items.map(({ node, date, reason }) => (
					<li key={node.id} className="-mx-2 flex items-baseline gap-2 rounded-sm px-2 py-px hover:bg-hover">
						<Checkbox node={node} onToggle={() => toggleDone(node)} />
						<Link
							to="/n/$nodeId"
							params={{ nodeId: node.id }}
							className={`min-w-0 flex-1 truncate transition-colors ${
								isTerminal(node.status) ? "text-ink-faint line-through" : ""
							}`}
						>
							{node.title || "без названия"}
						</Link>
						<span className="tabular shrink-0 text-xs font-light text-danger">
							{reason === "day" ? "день" : "срок"} {shortDate(date, today)}
						</span>
					</li>
				))}
			</ListPage>
		);
	}
});

/**
 * Архив — ЛИНЗА и единственное место, которое требует сети: в реплику архив не входит
 * (offline-sync §5). Оффлайн честно показывает, что связи нет.
 */
const archiveRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/archive",
	component: function ArchivePage() {
		return <ArchiveView />;
	}
});

const conflictsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/conflicts",
	component: function ConflictsPage() {
		const conflicts = useConflicts();
		const store = useStore();
		return (
			<ListPage title="Конфликты" empty="карантин пуст">
				{conflicts.map((conflict) => (
					<li key={conflict.opId} className="border-b border-stroke-soft py-3 last:border-b-0">
						<div className="font-medium text-danger">⚠ {intentLabel(conflict.name)}</div>
						<div className="mt-1 text-sm text-ink-muted">{conflict.message}</div>
						<div className="mt-2 flex gap-3">
							{/* «Применить заново», а не «повторить»: это НОВАЯ операция с новым opId
							    и заново взятыми базами — тот же opId навсегда вернул бы прежний отказ. */}
							<button
								type="button"
								className="text-xs font-light text-ink-muted transition-colors hover:text-ink-strong"
								onClick={() => store.discardConflict(conflict.opId)}
							>
								выбросить операцию
							</button>
						</div>
					</li>
				))}
			</ListPage>
		);
	}
});

function ListPage({ title, empty, children }: { title: string; empty: string; children: React.ReactNode }) {
	const items = Array.isArray(children) ? children : [children];
	const phone = usePhone();
	return (
		<div className="panel flex h-full min-w-0 flex-1 flex-col">
			<header className="page-header shrink-0 px-4 pt-4 pb-2 md:px-8 md:pt-6 md:pb-3">
				<h1 className="text-2xl font-bold tracking-tight text-ink-strong md:text-3xl">{title}</h1>
			</header>
			<div className="page-scroll min-h-0 flex-1 overflow-y-auto px-4 pb-12 md:px-8">
				{items.flat().length === 0 ? (
					<p className="text-sm font-light text-ink-muted">{empty}</p>
				) : (
					<ul>{children}</ul>
				)}
			</div>
			{/* Панель действий — замена клавиатуры на телефоне, и на десктопе её быть не
			    должно: там она вылезала пустой кнопкой «Меню» поверх страницы. Аутлайна
			    списочные страницы не держат, поэтому и на телефоне в ней только шторка —
			    это честно: делать здесь больше нечего. */}
			{phone && <ActionRail />}
		</div>
	);
}

function ArchiveView() {
	const phone = usePhone();
	return (
		<div className="panel flex h-full min-w-0 flex-1 flex-col">
			<header className="page-header shrink-0 px-4 pt-4 pb-2 md:px-8 md:pt-6 md:pb-3">
				<h1 className="text-2xl font-bold tracking-tight text-ink-strong md:text-3xl">Архив</h1>
				<p className="mt-1 text-xs font-light text-ink-muted">
					линза; требует сети — в локальную реплику архив не входит
				</p>
			</header>
			<div className="page-scroll min-h-0 flex-1 overflow-y-auto px-4 pb-12 md:px-8">
				<ArchiveTree />
			</div>
			{/* Как и на списочных страницах: рельса — телефонная, на десктопе не нужна. */}
			{phone && <ActionRail />}
		</div>
	);
}

function intentLabel(name: string): string {
	const labels: Record<string, string> = {
		moveNodes: "не удалось переместить",
		archiveNodes: "не удалось заархивировать",
		restoreNodes: "не удалось вернуть из архива",
		deleteNodes: "не удалось удалить",
		renameNode: "текст разошёлся",
		setBody: "заметка разошлась",
		addPlacement: "не удалось вплести",
		createNode: "не удалось создать"
	};
	return labels[name] ?? name;
}

export const routeTree = rootRoute.addChildren([
	indexRoute,
	todayRoute,
	dayRoute,
	inboxRoute,
	pinnedRoute,
	focusRoute,
	nodeRoute,
	projectsRoute,
	calendarRoute,
	recurringRoute,
	overdueRoute,
	archiveRoute,
	conflictsRoute
]);

export const router = createRouter({ routeTree, defaultPreload: false });

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}
